import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Panel from '../../components/shared/Panel';
import { Button, Form, Modal, Popup } from 'semantic-ui-react';
import { PaxInput } from '../../components/PaxInput';
import { useMessageResources, useTextResources } from '../../language';
import WingDropDown from '../../components/shared/WingDropDown';
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import { useCurrentHotel } from '../../hooks/useCurrentHotel';
import CustomerDropDown from '../../components/shared/CustomerDropDown';
import PaymentTypesDropDown from '../../components/shared/PaymentTypesDropDown';
import NumericInput from '../../components/shared/NumericInput';
import GridPanel from '../../components/GridPanel';
import { EntityType, MenuActions } from '../../components/shared/constants';
import { useLoader } from '../../hooks/useLoader';
import { onError, onSuccess } from '../../components/shared/messages';
import { HotelDefaultReservationValues, Rates, RoomTypes } from '../../api/services/next';
import RatesDropDown from '../../components/shared/RatesDropDown';
import { Currencies } from '../../components/shared/currencies';
import ProductsDropDown from '../../components/shared/ProductsDropDown';
import { useConfirm } from '../../hooks/useConfirm';

export const DefaultReservationValues = () => {
    const text = useTextResources();
    const message = useMessageResources();
    const loader = useLoader();
    const confirm = useConfirm();
    var currentHotel = useCurrentHotel();
    var hotelId = currentHotel?.Id || 0;

    const [defaultReservationValues, setDefaultReservationValues] = useState({
        HotelId: hotelId,
        Nights: 2
    });
    const [defaultReservationProducts, setDefaultReservationProducts] = useState([]);
    const [roomTypes, setRoomTypes] = useState([]);
    const [rates, setRates] = useState([]);

    // Individual setters for each member
    const setWingId = (WingId) =>
        setDefaultReservationValues(prev => ({ ...prev, WingId }));
    const setRoomTypeId = (RoomTypeId) =>
        setDefaultReservationValues(prev => ({ ...prev, RoomTypeId }));
    const setAgentId = (AgentId) =>
        setDefaultReservationValues(prev => ({ ...prev, AgentId }));
    const setCompanyId = (CompanyId) =>
        setDefaultReservationValues(prev => ({ ...prev, CompanyId }));
    const setPaymentTypeId = (PaymentTypeId) =>
        setDefaultReservationValues(prev => ({ ...prev, PaymentTypeId }));
    // setPax sets Adults, Children, Infants from the object
    const setPax = (pax) =>
        setDefaultReservationValues(prev => ({
            ...prev,
            Adults: pax.Adults,
            Children: pax.Children,
            Infants: pax.Infants
        }));
    const setNights = (Nights) =>
        setDefaultReservationValues(prev => ({ ...prev, Nights }));

    const WingSpec = useMemo(() => ({ HotelId: hotelId }), [hotelId]);
    const RoomTypesSpec = useMemo(() => ({ HotelId: hotelId, WingId: defaultReservationValues.WingId }), [hotelId, defaultReservationValues.WingId]);
    const AgentsSpec = useMemo(() => ({ CustomerTypeId: 2, GetOnlyCustomerArray: true }), []);
    const CompaniesSpec = useMemo(() => ({ CustomerTypeId: 1, GetOnlyCustomerArray: true }), []);
    const genericFieldAndValueDropDownStyles = { position: 'absolute', marginTop: '-17px', width: '20%' };
    const defaultInputProps = { search: true, style: genericFieldAndValueDropDownStyles };

    const roomTypeFormatter = useCallback(({ row }) => {
        if (roomTypes?.length && row) {
            // Get all selected room type IDs except the current row's selection
            const selectedRoomTypeIds = defaultReservationProducts
                .filter(p => p !== row && p.RoomTypeId)
                .map(p => p.RoomTypeId);
            
            // Filter room types to exclude already-selected ones (but keep current row's selection)
            const availableRoomTypes = roomTypes.filter(rt => 
                !selectedRoomTypeIds.includes(rt.Id) || rt.Id === row.RoomTypeId
            );

            return <RoomTypesDropDown
                {...defaultInputProps}
                placeholder={text("RoomType")}
                data={availableRoomTypes}
                onChange={(value) => {
                    row.RoomTypeId = value;
                    setDefaultReservationProducts(prev => {
                        const updatedProducts = [...prev];
                        return updatedProducts;
                    })
                }}
                value={row.RoomTypeId}
            />
        } else {
            return null;
        }
    }, [roomTypes, defaultReservationProducts, defaultInputProps, text]);

    const rateFormatter = ({ row }) => {
        if (row.RoomTypeId && rates?.length) {
            const ratesForRoomType = rates.filter(rate =>
                rate.RatePricingElements != null &&
                rate.RatePricingElements.some(element => element.EntityTypeId === EntityType.RoomType && element.EntityId === row.RoomTypeId)
            );
            return <RatesDropDown
                {...defaultInputProps}
                placeholder={text("Rate")}
                onChange={(value) => {
                    row.RateId = value;
                    row.CurrencyId = rates.find(rate => rate.Id === value)?.CurrencyId || null;
                    setDefaultReservationProducts(prev => {
                        const updatedProducts = [...prev];
                        return updatedProducts;
                    })
                }}
                value={row.RateId}
                data={ratesForRoomType}
            />
        } else {
            return null;
        }
    };

    const currencyFormatter = ({ row }) => {
        if (row.CurrencyId) {
            return `${Currencies[row.CurrencyId].name} (${Currencies[row.CurrencyId].symbol})`;
        } else {
            return null;
        }
    }

    const productFormatter = ({ row }) => {
        if (row?.RoomTypeId && row?.RateId) {
            return <ProductsDropDown
                {...defaultInputProps}
                placeholder={text("Product")}
                spec={{ HotelId: hotelId, RoomTypeId: row.RoomTypeId, RateId: row.RateId }}
                onChange={(value) => {
                    row.ProductId = value;
                    setDefaultReservationProducts(prev => {
                        const updatedProducts = [...prev];
                        return updatedProducts;
                    })
                }}
                value={row.ProductId}
            />
        } else {
            return null;
        }
    }
    const confirmDeletion = useCallback(() => confirm({
        header: message('AreYouSure'),
        content: message('AreYouSureYouWouldLikeToDeleteSelectedItems'),
        confirmButton: {
            content: text('Yes'),
            positive: true,
        },
        cancelButton: {
            content: text('No'),
            negative: true,
        },
    }), []);

    const confirmAddRoomtypes = useCallback(({ onConfirm }) => {
        let rateId = null;
        confirm({
            header: text('AddRoomtypes'),
            content: <Modal.Content>
                <Form>
                    <Form.Field
                        label={text("Rate")}
                        placeholder={text('Select')}
                        control={RatesDropDown}
                        spec={{ HotelId: hotelId }}
                        width={16}
                        onChange={value => rateId = value}
                    />
                </Form>
            </Modal.Content>,
            confirmButton: {
                content: text('OK'),
                positive: true,
                onClick: () => onConfirm(rateId)
            },
            cancelButton: {
                content: text('Cancel'),
                negative: true,
            },
        })
    }, [hotelId, text, confirm]);

    const onGridUpdate = async (added, changed, deleted) => {
        if (deleted?.length > 0 && await confirmDeletion()) {
            setDefaultReservationProducts(prev => {
                return prev.filter((product, index) => !deleted.includes(index));
            });
        }
    }

    const addMultipleRoomtypes = useCallback(async () => {
        await confirmAddRoomtypes({
            onConfirm: (selectedRateId) => {
                if (selectedRateId) {
                    const rate = rates.find(rate => rate.Id === selectedRateId);
                    if (rate) {
                        // Get existing room type IDs to avoid duplicates
                        const existingRoomTypeIds = defaultReservationProducts
                            .filter(p => p.RoomTypeId)
                            .map(p => p.RoomTypeId);

                        const roomTypesForRate = roomTypes.filter(roomType =>
                            rate.RatePricingElements != null &&
                            rate.RatePricingElements.some(element => element.EntityTypeId === EntityType.RoomType && element.EntityId === roomType.Id) &&
                            // Skip room types already in the grid
                            !existingRoomTypeIds.includes(roomType.Id)
                        );
                        const newProducts = roomTypesForRate.map(roomType => ({
                            RoomTypeId: roomType.Id,
                            RateId: rate.Id,
                            CurrencyId: rate.CurrencyId,
                            ProductId: null
                        }));
                        setDefaultReservationProducts(prev => {
                            const updatedProducts = [...prev, ...newProducts];
                            return updatedProducts;
                        });
                    }
                }
            }
        });
    }, [confirmAddRoomtypes, rates, roomTypes, defaultReservationProducts]);


    const gridOptions = {
        columns: [
            { name: 'RoomTypeId', title: text('RoomType'), formatter: roomTypeFormatter },
            { name: 'RateId', title: text('Rate'), formatter: rateFormatter },
            { name: 'CurrencyId', title: text('Currency'), formatter: currencyFormatter },
            { name: 'ProductId', title: text('Product'), formatter: productFormatter },
        ],
        editable: {
            allowAdd: true,
            allowDelete: true,
            extraProps: {
                addedRows: [],
                onAddedRowsChange: addedRows => {
                    setDefaultReservationProducts(prev => {
                        return [...prev, ...addedRows];
                    });
                }
            }
        },
        additionalCommands: [
            <Popup trigger={<Button icon="list" onClick={addMultipleRoomtypes} disabled={rates.length === 0}></Button>} content={text('AddRoomtypes')} key="add-roomtypes" />
        ],
    };

    useEffect(() => {
        (async () => {
            try {
                loader(true);
                const result = await HotelDefaultReservationValues.GetDefaultReservationValues({
                    HotelId: hotelId
                });
                if (result) {
                    setDefaultReservationValues(result);
                }
                const productsResult = await HotelDefaultReservationValues.GetDefaultReservationProducts({
                    HotelId: hotelId
                });
                if (productsResult) {
                    setDefaultReservationProducts(productsResult);
                }
                const roomTypesResult = await RoomTypes.GetRoomTypes({
                    HotelId: hotelId
                });
                if (roomTypesResult) {
                    setRoomTypes(roomTypesResult);
                }
                const ratesResult = await Rates.GetRates({
                    HotelId: hotelId,
                    includes: ["RatePricingElements"]
                });
                if (ratesResult) {
                    setRates(ratesResult);
                }
            } catch (error) {
                onError(error);
            }
            finally {
                loader(false);
            }
        })()
    }, [])

    const save = useCallback(async () => {
        try {
            loader(true);
            const result = await HotelDefaultReservationValues.UpdateDefaultReservationValues(defaultReservationValues);
            if (result) {
                setDefaultReservationValues(prev => {
                    return {
                        ...prev,
                        ...result
                    }
                });
            }
           
            const productsResult = await HotelDefaultReservationValues.UpdateDefaultReservationProducts(hotelId, defaultReservationProducts);
            if (productsResult) {
                setDefaultReservationProducts(productsResult);
            }
            onSuccess();
        } catch (error) {
            onError(error);
        } finally {
            loader(false);
        }
    }, [loader, defaultReservationValues, hotelId, defaultReservationProducts]);

    useEffect(() => {
        if (window.optima && window.optima.commands) {
            window.optima.commands.unSubscribeAll(MenuActions.save);
            window.optima.commands.subscribe(MenuActions.save, save);
        }
    }, [save]);

    return <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Panel header="Default Reservation Values" style={{ flex: 'none' }}>
            <Form>
                <Form.Group>
                    <Form.Field
                        label={text("Wing")}
                        required
                        spec={WingSpec}
                        placeholder={text('Select')}
                        onChange={setWingId}
                        control={WingDropDown}
                        width={3}
                        value={defaultReservationValues.WingId}
                    />
                    <Form.Field
                        label={text("RoomType")}
                        required
                        placeholder={text('Select')}
                        control={RoomTypesDropDown}
                        spec={RoomTypesSpec}
                        onChange={setRoomTypeId}
                        value={defaultReservationValues.RoomTypeId}
                        width={3}
                    />
                    <Form.Field
                        label={text("Pax")}
                        required
                        control={PaxInput}
                        onChange={setPax}
                        nullable
                        value={{
                            Adults: defaultReservationValues.Adults,
                            Children: defaultReservationValues.Children,
                            Infants: defaultReservationValues.Infants
                        }}
                        width={2}
                    />
                    <Form.Field
                        label={text("Nights")}
                        required
                        control={NumericInput}
                        width={2}
                        placeholder={text('Nights')}
                        digits={0}
                        min={1}
                        max={365}
                        buttonPlacement="none"
                        onChange={setNights}
                        value={defaultReservationValues.Nights}
                    />
                </Form.Group>
                <Form.Group>
                    <Form.Field
                        control={CustomerDropDown}
                        required
                        clearable
                        spec={AgentsSpec}
                        label={text('Agent')}
                        placeholder={text('Select')}
                        value={defaultReservationValues.AgentId}
                        onChange={setAgentId}
                        width={3}
                    />
                    <Form.Field
                        control={CustomerDropDown}
                        clearable
                        required
                        spec={CompaniesSpec}
                        placeholder={text('Select')}
                        label={text('Company')}
                        value={defaultReservationValues.CompanyId}
                        onChange={setCompanyId}
                        width={3}
                    />
                    <Form.Field
                        placeholder={text('Select')}
                        label={text('PaymentType')}
                        clearable
                        required
                        control={PaymentTypesDropDown}
                        value={defaultReservationValues.PaymentTypeId}
                        onChange={setPaymentTypeId}
                        width={3}
                    />
                </Form.Group>
            </Form>
        </Panel>

        <GridPanel options={gridOptions} data={defaultReservationProducts} onUpdate={onGridUpdate} />
    </div>
}
